"use client";

import React, { useState, useMemo } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ArrowRight, Leaf } from "lucide-react";
const CATEGORIES = ["All", "Native", "Hardscape", "Seasonal", "Modern", "Low-Maintenance"];
const DESIGNS = [{
  id: 1,
  styleName: "NATIVE PRAIRIE",
  category: "Native",
  description: "Low-water, high-impact design utilizing indigenous plants to support local ecosystems while significantly reducing ongoing maintenance costs.",
  image: {
    src: "/img/design_native_prairie.png",
    srcset: "/img/design_native_prairie.png-wvc-srcset",
    sizes: "/img/design_native_prairie.png-wvc-sizes",
    alt: "Native prairie landscaping design with drought-resistant plants"
  }
}, {
  id: 2,
  styleName: "MODERN MINIMALIST",
  category: "Modern",
  description: "Clean lines, geometric hardscaping, and structured plantings create a sophisticated, uncluttered outdoor living space perfect for entertaining.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_modern_minimalist.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_modern_minimalist.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_modern_minimalist.png-wvc-sizes",
    alt: "Modern minimalist backyard with concrete pavers and structured grass"
  }
}, {
  id: 3,
  styleName: "STONE & WATER",
  category: "Hardscape",
  description: "Elegant integration of natural stone patios, retaining walls, and subtle water features that provide a tranquil, enduring foundation.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_stone_water.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_stone_water.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_stone_water.png-wvc-sizes",
    alt: "Hardscape design featuring natural stone patio and small waterfall"
  }
}, {
  id: 4,
  styleName: "EVERGREEN OASIS",
  category: "Low-Maintenance",
  description: "Strategic use of slow-growing evergreens and premium mulch beds to ensure year-round color with minimal seasonal cleanup required.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_evergreen_oasis.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_evergreen_oasis.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_evergreen_oasis.png-wvc-sizes",
    alt: "Low maintenance garden with various evergreen shrubs"
  }
}, {
  id: 5,
  styleName: "FOUR-SEASON BLOOM",
  category: "Seasonal",
  description: "Carefully orchestrated planting schedules ensuring continuous waves of color and texture from early spring through late autumn.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_four_season.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_four_season.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_four_season.png-wvc-sizes",
    alt: "Garden showing vibrant seasonal blooming flowers"
  }
}, {
  id: 6,
  styleName: "ECO-TERRACE",
  category: "Hardscape",
  description: "Permeable paving solutions combined with native groundcovers to manage runoff beautifully while expanding usable outdoor square footage.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_eco_terrace.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_eco_terrace.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/design_eco_terrace.png-wvc-sizes",
    alt: "Eco-friendly terraced hardscape with permeable pavers"
  }
}];
export default function TrendingLandscapingIdeas() {
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const filteredDesigns = useMemo(() => {
    if (activeCategory === "All") return DESIGNS;
    return DESIGNS.filter(design => design.category === activeCategory);
  }, [activeCategory]);
  return <section id="trendinglandscapingideas" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-lg mb-4">
            <Leaf className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            Trending Design Concepts
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Explore our curated collection of elite landscaping styles. From drought-tolerant native havens to structured modern retreats, find the perfect inspiration for your property.
          </p>
        </div>

        {}
        <div className="flex justify-center mb-12 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
          <ToggleGroup type="single" value={activeCategory} onValueChange={value => value && setActiveCategory(value)} className="bg-card p-1 rounded-lg shadow-sm border border-border/50">
            {CATEGORIES.map((category, index) => <ToggleGroupItem key={category} value={category} data-index={index} className="px-6 py-2 rounded-md data-[state=on]:bg-primary data-[state=on]:text-primary-foreground font-medium transition-colors">
                {category}
              </ToggleGroupItem>)}
          </ToggleGroup>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredDesigns.map((design, index) => <Card key={design.id} data-index={index} className="group overflow-hidden border-border/40 shadow-md hover:shadow-xl transition-all duration-300 bg-card flex flex-col h-full">
              <div className="relative overflow-hidden">
                <AspectRatio ratio={4 / 3}>
                  <img src={design.image.src} data-wvc-srcset={design.image.srcset} data-wvc-sizes={design.image.sizes} alt={design.image.alt} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </AspectRatio>
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <Badge variant="secondary" className="absolute top-4 left-4 font-sans font-semibold tracking-wide shadow-sm backdrop-blur-md bg-background/90 text-foreground">
                  {design.category}
                </Badge>
              </div>

              <CardHeader className="pb-4 flex-grow">
                <CardTitle className="font-default text-xl md:text-2xl font-bold tracking-tight uppercase text-foreground">
                  {design.styleName}
                </CardTitle>
                <CardDescription className="font-sans text-base text-muted-foreground leading-relaxed mt-2">
                  {design.description}
                </CardDescription>
              </CardHeader>

              <CardFooter className="pt-0 mt-auto">
                <Button asChild className="w-full group/btn" variant="default">
                  <Link to="#">
                    Request Design Consultation
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
        
        {filteredDesigns.length === 0 && <div className="text-center py-20">
            <p className="text-muted-foreground font-sans text-lg">No designs found for this category.</p>
          </div>}
      </div>
    </section>;
}